import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pcLogo from '../../../../assets/Images/pc-logo.png';
import { useQRCodeVerification } from '../../hooks/useQRCodeVerification';
import './PolicyVerificationSuccessSection.css';

const PolicyVerificationSuccessSection: React.FC = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState<string>('');
  const { verifyQRCode, loading, data } = useQRCodeVerification();

  // Extract verification ID from URL path
  const extractVerificationIdFromUrl = (): string | null => {
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/');
    const validationSuccessIndex = pathSegments.findIndex(segment => segment === 'validation-success');
    
    if (validationSuccessIndex !== -1 && validationSuccessIndex + 1 < pathSegments.length) {
      return pathSegments[validationSuccessIndex + 1];
    }
    
    return null;
  };

  // Format current timestamp
  const formatCurrentTime = (): string => {
    const now = new Date();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const year = now.getFullYear();
    
    // Convert to 12-hour format with proper AM/PM
    const hours24 = now.getHours();
    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const formattedHours = hours12.toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    
    return `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  // Initialize verification process
  useEffect(() => {
    const initializeVerification = async () => {
      setCurrentTime(formatCurrentTime());
      
      // Extract verification ID from URL
      const extractedId = extractVerificationIdFromUrl();
      
      console.log('PolicyVerificationSuccessSection: Extracted verification ID:', extractedId);
      console.log('PolicyVerificationSuccessSection: Current URL:', window.location.pathname);
      
      if (extractedId) {
        try {
          console.log('PolicyVerificationSuccessSection: Starting verification for ID:', extractedId);
          const response = await verifyQRCode(extractedId);
          
          // Handle API errors (500 status for invalid IDs)
          if (!response.success || response.code === 500) {
            console.error('QR Code verification failed:', response.message);
            navigate('/customer-portal/transaction/validation-invalid');
            return;
          }
          
          console.log('PolicyVerificationSuccessSection: Verification successful:', response);
        } catch (err) {
          console.error('QR Code verification error:', err);
          navigate('/customer-portal/transaction/validation-invalid');
        }
      } else {
        console.error('No verification ID found in URL');
        navigate('/customer-portal/transaction/validation-invalid');
      }
    };

    initializeVerification();
  }, [verifyQRCode, navigate]);

  // Handle View Data Privacy click
  const handleViewDataPrivacy = () => {
    navigate('customer-portal/privacy-statement');
  };

  // Loading state
  if (loading) {
    return (
      <div className="policy-verification-container">
        <div className="policy-verification-header">
          <img src={pcLogo} alt="Pacific Cross Logo" className="pc-logo" />
        </div>
        <div className="policy-verification-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <h2>Verifying Policy...</h2>
            <p>Please wait while we verify your policy information.</p>
          </div>
        </div>
      </div>
    );
  }

  // If no data is available, show error (this shouldn't happen as errors redirect to invalid page)
  if (!data) {
    return (
      <div className="policy-verification-container">
        <div className="policy-verification-header">
          <img src={pcLogo} alt="Pacific Cross Logo" className="pc-logo" />
        </div>
        <div className="policy-verification-content">
          <div className="loading-state">
            <h2>No Policy Data Available</h2>
            <p>Unable to retrieve policy information.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="policy-verification-container">
      {/* Header with Pacific Cross Logo */}
      <div className="policy-verification-header">
        <img src={pcLogo} alt="Pacific Cross Logo" className="pc-logo" />
      </div>

      {/* Main Content */}
      <div className="policy-verification-content">
        <h1 className="policy-verification-title">Policy Verification</h1>
        <p className="policy-verification-subtitle">View policy details below.</p>

        {/* Policy Details Card */}
        <div className="policy-card">
          {/* Card Header with Plan Name and Status */}
          <div className="policy-card-header">
            <h2 className="plan-name">Travelsafe</h2>
            <div className={`status-indicator ${data.coverage_status}`}>
              <span className="status-dot"></span>
              <span className="status-text">
                {data.coverage_status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Policy Details */}
          <div className="policy-details">
            <div className="policy-detail-row">
              <span className="detail-label">Plan Name</span>
              <span className="detail-value">{data.planName}</span>
            </div>
            
            <div className="policy-detail-row">
              <span className="detail-label">OCC Number</span>
              <span className="detail-value">{data.occNumber || 'TNDT01***********'}</span>
            </div>
            
            <div className="policy-detail-row">
              <span className="detail-label">Policy Holder</span>
              <span className="detail-value">{data.policyHolder}</span>
            </div>
            
            <div className="policy-detail-row">
              <span className="detail-label">Validity Period</span>
              <span className="detail-value">From: {data.validityPeriod}</span>
            </div>
          </div>

          {/* Data Privacy Section */}
        <div className='data-privacy-container'>
          <div className="data-privacy-section">
            <div className="policy-privacy-header">
              <div className='privacy-icon-cont'>
              <img 
                src="/travel-portal/public-assets/protection-icon.svg" 
                alt="Data Privacy & Protection" 
                className="privacy-icon"
              />
              </div>
              <span className="policy-verification-privacy-title">Data Privacy & Protection</span>
            </div>
            <p className="privacy-description">
              This policy information is handled in accordance with the Republic Act No. 10173 - Data Privacy Act of 2012 (DPA) of the Philippines.
            </p>
            <button 
              className="privacy-link"
              onClick={handleViewDataPrivacy}
            >
              <img 
                src="/travel-portal/public-assets/invalidqr-icon.svg" 
                alt="View Data Privacy" 
                className="privacy-link-icon"
              />
              View Data Privacy
            </button>
          </div>
          </div>
        </div>

        {/* Footer Information */}
        <div className="policy-footer">
          <p className="retrieval-info">
            Policy information retrieved on {currentTime}
          </p>
          
          <div className="contact-info">
            <p className="policy-verification-contact-text">
              For assistance and inquiries, you may reach out to our Customer Services Team.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <img 
                  src="/travel-portal/public-assets/phone-icon.svg" 
                  alt="Phone" 
                  className="contact-icon"
                />
                <span>+632 8230-8511</span>
              </div>
              <div className="contact-item">
                <img 
                  src="/travel-portal/public-assets/email-icon.svg" 
                  alt="Email" 
                  className="contact-icon"
                />
                <span>client_services@pacificcross.com.ph</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyVerificationSuccessSection;